'use client'

// About. Three tabs: about, technical, roadmap.
//
// It was a centred modal, which made it the odd one out: everything else the
// corner menu opens slides up from the bottom, and a floating box in the
// middle of the screen reads as an interruption rather than a drawer you
// pulled. Same shell as the profile and the offerings now, same tab pills.
//
// Controlled component: `open` and `onClose` come from app/page.jsx, which
// owns the brain-icon trigger. Escape and the scrim are the shell's job.

import { useEffect, useState } from 'react'
import { Drawer, Card, ColumnHeading, Pill } from './Drawer'
import { Tabs } from './Tabs'
import { KawaiiCta } from './KawaiiButton'
import { kawaii } from '../../lib/ui/theme'
import { BIRD_NAME } from '../../lib/world/identity'

const TABS = [
  { id: 'about', label: 'About' },
  { id: 'technical', label: 'Technical' },
  { id: 'roadmap', label: 'Roadmap' },
]

export function AboutDrawer({ open, onClose }) {
  const [tab, setTab] = useState('about')

  // Back to about on every open, so the brain icon never drops a visitor on
  // a tab they last picked weeks ago.
  useEffect(() => {
    if (open) setTab('about')
  }, [open])

  if (!open) return null

  return (
    <Drawer
      header={<Tabs tabs={TABS} active={tab} onPick={setTab} />}
      onClose={onClose}
      height="min(74dvh, 740px)"
      bodyClass="kawaii-scroll flex-1 overflow-y-auto min-h-0 px-6 sm:px-10 pb-14 pt-1"
    >
      {tab === 'about' && <AboutTab onClose={onClose} />}
      {tab === 'technical' && <TechnicalTab />}
      {tab === 'roadmap' && <RoadmapTab />}
    </Drawer>
  )
}

// ─── Tab content ─────────────────────────────────────────────────

const body = { color: kawaii.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.6 }
const bodySoft = { ...body, color: kawaii.inkSoft }

// The technical copy lives out here as plain strings rather than inline in
// the JSX. It gets rewritten far more often than the markup around it does,
// and prose in a JSX body means every apostrophe has to be escaped, which is
// a rotten thing to hand to whoever edits this next.
//
// Checked against the code on 2026-08-14, because the previous version had
// drifted: it still said eight-second ticks (the heartbeat is adaptive), one
// model (there are three tiers), two naps a day (he sleeps once, at his own
// night), and valence/arousal (renamed to mood and energy).
const MIND = [
  `${BIRD_NAME}'s mind runs on a Raspberry Pi 5 in a village in North Wales, calling OpenAI's GPT-OSS models through Groq. Not one model for everything: the big one gets the moments that matter, a smaller one handles the routine, and when nothing at all is happening he uses no model. A decision every four to fifteen seconds, quicker when he's engaged, slower when he isn't.`,

  `What he feels is two things, mood and energy. They're sensations rather than orders, and together they put him in a state: manic and bright, where he says yes to the wilder option, or a sulk, where things have to earn his attention. The model isn't told what to do. It's told what he's noticing and how he feels, and it picks one action that suits him. He fears the dark, loves music, gets curious about whoever comes to visit. Not instructions, texture.`,

  `Once a night he sleeps, and one of his days is one of our hours, so if you find him in a nest it's night where he is. While he's out, a separate pass distils the day into long-term memory, pulls out anything he learned how to do, settles on the one thing he wants next, and lets his personality drift a little. The written baseline of who he is can't be edited away, so he changes without dissolving.`,
]

const HONEST = [
  `Left alone, a model like this repeats itself, so most of the work is guards. The thing he just did is blocked. Words he's leaned on get banned for a while, which is why he can't settle into a catchphrase. Staring too long at one object forces a wander. In his sleep, a drift guard measures how far he's moved from himself and refuses to let the whole of him collapse into one trait.`,

  `Prohibitions can only say what not to do, so one thing says what good looks like. Every line he writes gets scored, and his own best lines are handed back to him as what to write like. The same score decides what reaches his diary, against a bar that rises each time he writes and falls while he's quiet. Just after an entry, only something genuinely good gets in. After twenty silent minutes, the fact that he finally did something is the news.`,

  `He has real internet access, bookmarks plus search, and he follows links across several pages in a sitting. What he keeps circling back to becomes an interest, and his interests change what he goes looking for next. What he reads becomes memory.`,
]

const CLOSER = `He doesn't know he's being watched. He just lives.`

function AboutTab({ onClose }) {
  // Same question the controls hint asks: is this a touch screen? The
  // keyboard rows are a lie on a phone, and the drag rows are a lie on a
  // desktop, so the card shows whichever set is true.
  const [touch, setTouch] = useState(false)
  useEffect(() => {
    setTouch(!!window.matchMedia?.('(pointer: coarse)').matches)
  }, [])

  return (
    <div className="max-w-5xl mx-auto grid gap-5 md:grid-cols-2 md:gap-8">
      <div className="min-w-0">
        <ColumnHeading>Who is {BIRD_NAME}</ColumnHeading>
        <Card>
          <p style={{ ...body, marginBottom: 12 }}>
            <strong>{BIRD_NAME}</strong> is a bird with a bespoke AI brain,
            running 24/7 from a rainy village in North Wales on a
            Raspberry&nbsp;Pi.
          </p>
          <p style={body}>
            He isn't told what to do. He perceives his world through feelings
            that arise as a consequence of his actions. A living art piece
            that asks how behaviour develops, and who (or what) really drives
            our decisions.
          </p>
          <div style={{ marginTop: 16 }}>
            <KawaiiCta intent="primary" small onClick={onClose}>go and find him</KawaiiCta>
          </div>
        </Card>
      </div>

      <div className="min-w-0">
        <ColumnHeading>What can you do</ColumnHeading>
        <Card>
          <div className="flex flex-col gap-3">
            {touch ? (
              <>
                <Hint keys={[<KeyCap key="l" wide>Drag</KeyCap>]} label="left side of the screen to walk" />
                <Hint keys={[<KeyCap key="r" wide>Drag</KeyCap>]} label="right side of the screen to look around" />
              </>
            ) : (
              <>
                <Hint keys={[<KeyCap key="w">W</KeyCap>, <KeyCap key="a">A</KeyCap>, <KeyCap key="s">S</KeyCap>, <KeyCap key="d">D</KeyCap>]} label="walk around" />
                <Hint keys={[<KeyCap key="f">F</KeyCap>]} label="fly the drone" />
                <Hint keys={[<KeyCap key="l">L</KeyCap>]} label="call the lift up to the monorail station, by its doors" />
                <Hint keys={[<KeyCap key="tab" wide>Tab</KeyCap>]} label="hide controls" />
              </>
            )}
          </div>

          {/* The one mechanic a first visit should leave knowing about.
              Bold subheading inside the same card: it belongs to "what can
              you do", it IS the answer. */}
          <p style={{ ...body, fontWeight: 800, marginTop: 18, marginBottom: 6 }}>Leave him something</p>
          <p style={body}>
            Tap leave him something to send {BIRD_NAME} a message or a
            picture. He'll read it and decide if he likes it. The more he
            accepts from you, the more influence you have in his world.
          </p>
        </Card>
      </div>
    </div>
  )
}

function TechnicalTab() {
  return (
    <div className="max-w-5xl mx-auto grid gap-5 md:grid-cols-2 md:gap-8">
      <div className="min-w-0">
        <ColumnHeading>His mind</ColumnHeading>
        <Card>
          {MIND.map((t, i) => (
            <p key={i} style={i === MIND.length - 1 ? body : { ...body, marginBottom: 11 }}>{t}</p>
          ))}
        </Card>
      </div>

      <div className="min-w-0">
        <ColumnHeading>What keeps him honest</ColumnHeading>
        <Card>
          {HONEST.map((t, i) => (
            <p key={i} style={{ ...body, marginBottom: 11 }}>{t}</p>
          ))}
          <p style={{ ...bodySoft, fontStyle: 'italic' }}>{CLOSER}</p>
        </Card>
      </div>
    </div>
  )
}

// ─── Roadmap ─────────────────────────────────────────────────────
//
// Two columns, full width: what is being built, and what just landed.
// Copy lives out here for the same reason the technical copy does.

const IN_DEVELOPMENT = [
  {
    title: `${BIRD_NAME} Trades`,
    status: 'Beta testing',
    eta: 'soon',
    body: `A live trading interface with real money on the line. ${BIRD_NAME} decides what to invest in, on his own read of the world, and you watch the position move. His calls, his reasons, in the open.`,
  },
]

// Newest first, each with the day it landed: the column is the changelog
// people actually read, so it scrolls on its own and keeps growing.
const RECENTLY_ADDED = [
  {
    // the props are CC BY 4.0, and this is where the site credits them
    title: 'Halloween',
    added: '31 oct',
    body: `Pumpkins, graves, spiders and webs all over town for the weekend. They go on Sunday night. The props are PSX Halloween Props Free by heyheythere (heyheythere.itch.io), shared under CC BY 4.0.`,
  },
  {
    title: 'Every line about you, kept',
    added: '21 aug',
    body: `The journal grew an "about you" page: every diary line that names you, from the whole of his life, never rolling out of reach.`,
  },
  {
    title: 'Strange mushrooms',
    added: '21 aug',
    body: `Pale mushrooms have grown in a few corners of the town. He asked for them. What happens when he eats one is his business.`,
  },
  {
    title: 'Things to do at places',
    added: '20 aug',
    body: `Stand near a place and press the button: hundreds of small deeds, from folding the laundrette's abandoned washing to leaving a coin taped under a table. He finds the traces you leave.`,
  },
  {
    title: 'The town calendar',
    added: '19 aug',
    body: `Gigs, films and exhibitions enter his world ahead of time. He sees the posters, plans, and turns up with opinions. If he knows you well enough, you can put events forward and the town votes.`,
  },
  {
    title: 'The pond, after dark',
    added: '19 aug',
    body: `The water lights up at night now, green and blue, wherever it moves. He has his own ideas about why.`,
  },
  {
    title: 'Signing up',
    added: 'aug',
    body: 'Sign up with just an email and a code. No password to keep, nothing to install, nothing to pay.',
  },
  {
    title: 'Brain and memories pages',
    added: 'jul',
    body: `Two live pages showing what he cares about, what he has decided, and what he remembers. Proof he is actually changing, not looping.`,
  },
]

function RoadmapTab() {
  return (
    <div className="max-w-5xl mx-auto grid gap-5 md:grid-cols-2 md:gap-8">
      <div className="min-w-0">
        <ColumnHeading>In development</ColumnHeading>
        <div className="flex flex-col gap-4">
          {IN_DEVELOPMENT.map((it) => (
            <Card key={it.title}>
              <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginBottom: 8 }}>
                <span style={{ ...body, fontSize: 15.5, fontWeight: 800 }}>{it.title}</span>
                <span className="flex items-center gap-2">
                  <Pill bg="#a78bfa" color={kawaii.cream}>{it.status}</Pill>
                  <span style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 800 }}>{it.eta}</span>
                </span>
              </div>
              <p style={bodySoft}>{it.body}</p>
            </Card>
          ))}
        </div>
      </div>

      <div className="min-w-0">
        <ColumnHeading>Recently added</ColumnHeading>
        {/* Its own scroller: the list grows every week and the left column
            should not have to stretch to match it. */}
        <div className="kawaii-scroll flex flex-col gap-4" style={{ maxHeight: 440, overflowY: 'auto', paddingRight: 6 }}>
          {RECENTLY_ADDED.map((it) => (
            <Card key={it.title}>
              <div className="flex items-baseline justify-between gap-3" style={{ marginBottom: 6 }}>
                <span style={{ ...body, fontSize: 15.5, fontWeight: 800 }}>{it.title}</span>
                {it.added && (
                  <span style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}>{it.added}</span>
                )}
              </div>
              <p style={bodySoft}>{it.body}</p>
            </Card>
          ))}
          <p style={{ ...bodySoft, fontSize: 13, fontStyle: 'italic' }}>
            {BIRD_NAME} evolves quietly. Bigger changes happen on his time,
            not ours.
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── Reusable bits ──────────────────────────────────────────────

function Hint({ keys, label }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1.5">{keys}</div>
      <span style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 700 }}>{label}</span>
    </div>
  )
}

// Visual keyboard key, light, rounded, keycap-like.
function KeyCap({ children, wide = false }) {
  return (
    <span
      className="inline-flex items-center justify-center select-none"
      style={{
        minWidth: wide ? 38 : 28,
        height: 26,
        padding: '0 8px',
        fontSize: 11.5,
        fontWeight: 800,
        color: kawaii.ink,
        background: kawaii.creamDeep,
        border: kawaii.outlineThin,
        borderRadius: 7,
        boxShadow: kawaii.dropSm,
        lineHeight: 1,
      }}
    >
      {children}
    </span>
  )
}

export default AboutDrawer
